import React, { useState, useRef, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageContext } from '../hooks/useLanguage';
import { chatbotResponses, keywordMap } from '../data/chatbotData';
import { ChatMessage } from '../types';

const Chatbot: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'bot', content: chatbotResponses[lang].greeting }]);
    }
  }, [isOpen, lang]);

  const getBotResponse = (text: string): string => {
    const lower = text.toLowerCase();
    for (const [key, kws] of Object.entries(keywordMap)) {
      if (kws.some((kw) => lower.includes(kw))) {
        return chatbotResponses[lang][key as keyof typeof chatbotResponses.es] || chatbotResponses[lang].default;
      }
    }
    return chatbotResponses[lang].default;
  };

  const handleSend = () => {
    if (!input.trim()) return;
    const userMsg: ChatMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    setTimeout(() => {
      const botMsg: ChatMessage = { role: 'bot', content: getBotResponse(userMsg.content) };
      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 800);
  };

  return (
    <div style={{ position: 'fixed', bottom: '28px', right: '28px', zIndex: 999 }}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            style={{
              width: '360px',
              height: '480px',
              background: '#1A1A1A',
              border: '1px solid rgba(200,169,110,0.2)',
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '12px',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 24px',
              background: '#0D0D0D',
              borderBottom: '1px solid rgba(200,169,110,0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <div style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: '13px',
                  letterSpacing: '2px',
                  color: '#FFFFFF',
                }}>
                  VATIO CENTRAL
                </div>
                <div style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '11px',
                  color: '#C8A96E',
                  marginTop: '2px',
                  letterSpacing: '1px',
                }}>
                  {lang === 'es' ? 'En línea' : lang === 'en' ? 'Online' : 'En ligne'} ●
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#555555',
                  cursor: 'pointer',
                  fontSize: '20px',
                  lineHeight: 1,
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#555555')}
              >
                ×
              </button>
            </div>

            {/* Messages */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}>
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div style={{
                    maxWidth: '80%',
                    padding: '12px 16px',
                    background: msg.role === 'user' ? '#C8A96E' : '#2D2D2D',
                    color: msg.role === 'user' ? '#0D0D0D' : '#AAAAAA',
                    fontFamily: 'var(--font-body)',
                    fontSize: '14px',
                    lineHeight: 1.6,
                  }}>
                    {msg.content}
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    display: 'flex',
                    gap: '4px',
                    padding: '12px 16px',
                    background: '#2D2D2D',
                    width: 'fit-content',
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{ y: [0, -4, 0] }}
                      transition={{ repeat: Infinity, duration: 0.6, delay: i * 0.15 }}
                      style={{ width: '6px', height: '6px', background: '#C8A96E' }}
                    />
                  ))}
                </motion.div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div style={{
              padding: '16px',
              borderTop: '1px solid rgba(200,169,110,0.15)',
              display: 'flex',
              gap: '8px',
              background: '#0D0D0D',
            }}>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder={lang === 'es' ? 'Escribe un mensaje...' : lang === 'en' ? 'Type a message...' : 'Écrivez un message...'}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '1px solid rgba(255,255,255,0.06)',
                  background: '#1A1A1A',
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: 'var(--font-body)',
                  color: '#F0F0F0',
                }}
                onFocus={(e) => (e.target.style.borderColor = 'rgba(200,169,110,0.4)')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.06)')}
              />
              <motion.button
                onClick={handleSend}
                whileHover={{ background: '#C8A96E', color: '#0D0D0D' }}
                whileTap={{ scale: 0.95 }}
                style={{
                  padding: '12px 18px',
                  background: 'transparent',
                  color: '#C8A96E',
                  border: '1px solid #C8A96E',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '16px',
                  transition: 'all 0.2s',
                  fontFamily: 'var(--font-body)',
                }}
              >
                →
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ borderColor: '#E8C98A' }}
        whileTap={{ scale: 0.95 }}
        style={{
          width: '52px',
          height: '52px',
          background: isOpen ? '#1A1A1A' : '#0D0D0D',
          color: '#C8A96E',
          border: '1px solid #C8A96E',
          cursor: 'pointer',
          fontSize: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: 'auto',
          transition: 'all 0.2s',
        }}
      >
        {isOpen ? '×' : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        )}
      </motion.button>
    </div>
  );
};

export default Chatbot;
